/* ============================================================================
   bulk-form.js — the two-step bulk enquiry form
   ----------------------------------------------------------------------------
   WHY TWO STEPS AND NOT ONE LONG FORM
   Published research on form design is unusually consistent here: multi-step
   forms generate substantially more leads than the same questions shown all at
   once, and the gap is widest on mobile — which is where nearly all of this
   store's traffic is. The mechanism is not a trick. It is that:

     • A short first screen doesn't LOOK like work, so more people start.
     • The first screen asks about the shopper's own order — what they want and
       how many — which is interesting to answer.
     • Contact details are asked last, once someone has already invested effort.

   Form length is the single biggest cause of abandonment (~37% of drop-off),
   and roughly 37% of people abandon a form the moment it asks for a phone
   number unless the reason is obvious. That is why the phone field is on step
   two, and why it carries a line explaining exactly what the number is for.

   IF YOU EVER FLATTEN THIS BACK INTO ONE SCREEN, expect fewer enquiries.

   WHAT IT SUBMITS TO
   Nothing changes about where enquiries land: the whole form still posts to the
   same Google Form endpoint in one request, into the same Sheet. The steps are
   purely how the questions are presented.
   ========================================================================== */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.__bfInit) return;
    root.__bfInit = true;

    var form = root.querySelector('[data-bulk-gform]');
    var box = root.querySelector('[data-bulk-form]');
    var sink = root.querySelector('iframe');
    if (!form || !box || !sink) return;

    var steps = Array.prototype.slice.call(form.querySelectorAll('[data-step]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-step-dot]'));
    var err = form.querySelector('[data-form-err]');
    var at = 0;

    function show(i) {
      at = Math.max(0, Math.min(steps.length - 1, i));
      steps.forEach(function (s, k) { s.hidden = k !== at; });
      dots.forEach(function (d, k) {
        d.classList.toggle('is-on', k <= at);
        d.setAttribute('aria-current', k === at ? 'step' : 'false');
      });
      if (err) err.hidden = true;
      // move focus to the first control of the new step so keyboard and screen
      // reader users aren't left behind at the bottom of the previous one
      var first = steps[at].querySelector('input, select, textarea, button');
      if (first && at > 0) { try { first.focus({ preventScroll: true }); } catch (e) {} }
      box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function fail(msg, el) {
      if (err) { err.hidden = false; err.textContent = msg; }
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    /* Validate only the step being left — never show someone an error about a
       question they haven't been asked yet. */
    function validateStep(i) {
      var step = steps[i];
      var group = step.querySelector('[data-required-group]');
      if (group && !group.querySelector('input:checked')) {
        return fail(group.dataset.errorMsg || 'Please pick at least one option.', group);
      }
      var fields = step.querySelectorAll('input[required], select[required], textarea[required]');
      for (var k = 0; k < fields.length; k++) {
        if (!fields[k].checkValidity()) {
          fields[k].reportValidity();
          return false;
        }
      }
      if (err) err.hidden = true;
      return true;
    }

    root.querySelectorAll('[data-step-next]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (!validateStep(at)) return;
        show(at + 1);
        try {
          if (window.dataLayer) window.dataLayer.push({ event: 'bulk_form_step', step: at + 1 });
        } catch (e) {}
      });
    });
    root.querySelectorAll('[data-step-back]').forEach(function (b) {
      b.addEventListener('click', function () { show(at - 1); });
    });

    /* ---- submission ---- */
    var submitted = false;
    sink.addEventListener('load', function () {
      if (!submitted) return;                       // the iframe also fires on insertion
      box.classList.add('is-done');
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        var d = form.__payload || {};
        if (window.dataLayer) window.dataLayer.push({ event: 'bulk_enquiry', quantity: d.qty, purpose: d.purpose });
        if (window.gtag) window.gtag('event', 'generate_lead', { event_category: 'bulk', event_label: d.qty });
        if (window.fbq) window.fbq('track', 'Lead', { content_category: 'bulk' });
        if (window.mzEmit) window.mzEmit('bulk:enquiry', d);
      } catch (e) {}
    });

    form.addEventListener('submit', function (e) {
      if (!validateStep(at)) { e.preventDefault(); return; }
      /* Google date questions take three separate params, not an ISO string.
         Without this the required date is missing and Google throws the whole
         submission away while the iframe still reports success. */
      var dt = form.querySelector('[data-timeline]');
      if (dt && dt.value) {
        var parts = dt.value.split('-');
        var y = form.querySelector('[data-timeline-y]'), mo = form.querySelector('[data-timeline-m]'), da = form.querySelector('[data-timeline-d]');
        if (y) y.value = parts[0];
        if (mo) mo.value = String(parseInt(parts[1], 10));
        if (da) da.value = String(parseInt(parts[2], 10));
      }
      var q = form.querySelector('[data-qty-group] input:checked');
      var p = form.querySelector('[data-purpose-group] input:checked');
      form.__payload = { qty: q ? q.value : '', purpose: p ? p.value : '' };
      submitted = true;
      var btn = form.querySelector('[data-submit]');
      if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
    });

    /* ---- pre-fills from elsewhere on the page ---- */
    document.addEventListener('bulk:quantity', function (ev) {
      var n = ev.detail && ev.detail.quantity;
      if (!n) return;
      form.querySelectorAll('[data-qty-group] input').forEach(function (r) {
        var nums = (r.value.match(/\d+/g) || []).map(Number);
        if (!nums.length) return;
        var lo = nums[0], hi = nums.length > 1 ? nums[1] : Infinity;
        if (n >= lo && n <= hi) r.checked = true;
      });
    });

    document.addEventListener('bulk:purpose', function (ev) {
      var want = (ev.detail && ev.detail.purpose || '').toLowerCase();
      form.querySelectorAll('[data-purpose-group] input').forEach(function (r) {
        if (r.value.toLowerCase() === want) r.checked = true;
      });
    });

    /* ---- the shortlist ----
       Products tapped in the wall or the reel are collected here. They're shown
       back to the shopper as removable chips (people need to see that a tap
       registered), and written into a hidden field so they reach the Sheet —
       but only if a Shortlist field ID has been configured. */
    var picks = [];
    var picksRow = root.querySelector('[data-picks-row]');
    var picksBox = root.querySelector('[data-picks]');
    var picksInput = root.querySelector('[data-picks-input]');

    function renderPicks() {
      if (!picksBox) return;
      picksBox.innerHTML = '';
      picks.forEach(function (title, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bulkp-pick';
        b.innerHTML = '<span>' + title.replace(/[<>&]/g, '') + '</span><i aria-hidden="true">×</i>';
        b.setAttribute('aria-label', 'Remove ' + title);
        b.addEventListener('click', function () { picks.splice(i, 1); renderPicks(); });
        picksBox.appendChild(b);
      });
      if (picksRow) picksRow.hidden = picks.length === 0;
      if (picksInput) picksInput.value = picks.join(' · ');
    }

    document.addEventListener('bulk:pick', function (ev) {
      var t = ev.detail && ev.detail.title;
      if (!t || picks.indexOf(t) !== -1) return;
      picks.push(t);
      if (picks.length > 25) picks.shift();     // keep the field a sane length
      renderPicks();
    });

    /* The product reel sends these: someone spun to a toy and tapped
       "Add to my enquiry", so tick that product's category for them. */
    document.addEventListener('bulk:interest', function (ev) {
      var want = (ev.detail && ev.detail.category || '').toLowerCase();
      if (!want) return;
      var hit = false;
      form.querySelectorAll('[data-required-group] input').forEach(function (c) {
        if (c.value.toLowerCase() === want) { c.checked = true; hit = true; }
      });
      if (hit) root.dispatchEvent(new CustomEvent('bulk:interest-added', { bubbles: true, detail: ev.detail }));
    });

    show(0);
  }

  function boot() { document.querySelectorAll('[data-bulk-form-root]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();


/* ═══ MALI PANEL ═══════════════════════════════════════════════════════════
   Opens the chat with the shopper's picks already loaded, so they never retype
   what they just chose in the reel. The basket is read from the same hidden
   "picks" input the Google Form submits, which is why this works whether or
   not the form is visible. Every conversation lands in Mali's sales desk
   (<mali>/admin) with transcript, summary and lead score. */
(function () {
  var root = document.querySelector('[data-bulk-mali]');
  if (!root) return;
  var base = (root.getAttribute('data-mali-base') || '').replace(/\/$/, '');
  if (!base) return;
  var display = root.getAttribute('data-mali-display') || 'panel';
  var wrap = root.querySelector('[data-mali-wrap]');
  var frame = root.querySelector('[data-mali-frame]');
  var formWrap = document.querySelector('[data-bulk-formwrap]');

  function ctx() {
    var picks = document.querySelector('[data-picks-input]');
    var items = (picks && picks.value ? picks.value : '').split(/\s*[,\n]\s*/).filter(Boolean);
    var qtyEl = document.querySelector('input[name][type="radio"]:checked[value*="-"], input[type="radio"]:checked');
    var chosen = [];
    document.querySelectorAll('.bulkp-chip input:checked').forEach(function (i) { chosen.push(i.value); });
    var o = { source: 'bulk-page', products: items, interests: chosen };
    if (qtyEl && /\d/.test(qtyEl.value || '')) o.quantity = qtyEl.value;
    return o;
  }
  function url() { return base + '/?embed=1&ctx=' + encodeURIComponent(JSON.stringify(ctx())); }

  root.querySelector('[data-mali-open]').addEventListener('click', function () {
    if (display === 'tab') { window.open(url(), '_blank', 'noopener'); return; }
    if (frame && !frame.src) frame.src = url();
    if (frame) frame.hidden = false;
    if (!wrap) return;
    wrap.hidden = false;
    /* Full-screen on every device: a boxed frame leaves the chat a third of a
       phone screen, and on desktop it competes with the page behind it. Mali
       centres its own 960px card, so edge-to-edge is correct on wide screens. */
    wrap.classList.add('is-full');
    document.body.classList.add('bulkp-chat-open');
    try { history.pushState({ maliChat: 1 }, ''); } catch (e) {}
  });

  function closeChat(fromPop) {
    if (!wrap || !wrap.classList.contains('is-full')) return;
    wrap.classList.remove('is-full');
    wrap.hidden = true;
    document.body.classList.remove('bulkp-chat-open');
    /* the back gesture is how people close a full-screen thing on a phone */
    if (!fromPop) { try { if (history.state && history.state.maliChat) history.back(); } catch (e) {} }
  }
  var closeBtn = root.querySelector('[data-mali-close]');
  if (closeBtn) closeBtn.addEventListener('click', function () { closeChat(false); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeChat(false); });
  window.addEventListener('popstate', function () { closeChat(true); });
  var alt = root.querySelector('[data-mali-showform]');
  if (alt && formWrap) alt.addEventListener('click', function () {
    formWrap.hidden = false;
    formWrap.removeAttribute('data-form-collapsed');
    alt.hidden = true;
    formWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();


/* ═══ GOOGLE FORM FALLBACK — LINK MODE ══════════════════════════════════════
   TAOS's form returns 401 to anonymous POSTs, and the hidden-iframe submit
   cannot detect that: the shopper sees "Got it" and the enquiry is gone. In
   link mode the page collects nothing itself — it opens the real Google Form
   prefilled with whatever was picked in the reel, so Google handles validation
   and sign-in and nothing can be silently lost. */
(function () {
  var wrap = document.querySelector('[data-bulk-formwrap]');
  if (!wrap || wrap.getAttribute('data-form-mode') !== 'link') return;
  var view = wrap.getAttribute('data-gf-view') || '';
  if (!view) return;
  var form = wrap.querySelector('form');
  if (form) form.hidden = true;

  var picksInput = document.querySelector('[data-picks-input]');
  var itemsName = form && form.querySelector('.bulkp-chip input[type=checkbox]');
  itemsName = itemsName ? itemsName.getAttribute('name') : '';

  function url() {
    var qs = ['usp=pp_url'];
    var picks = picksInput && picksInput.value ? picksInput.value : '';
    if (picks && itemsName) {
      picks.split(/\s*[,\n]\s*/).filter(Boolean).forEach(function (v) {
        qs.push(encodeURIComponent(itemsName) + '=' + encodeURIComponent(v));
      });
    }
    return view + (view.indexOf('?') === -1 ? '?' : '&') + qs.join('&');
  }

  var btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'bulkp__btn bulkp__btn--ghost';
  btn.style.cssText = 'display:block;margin:6px auto 0;max-width:420px;width:100%';
  btn.textContent = wrap.getAttribute('data-gf-label') || 'Open the enquiry form';
  btn.addEventListener('click', function () {
    window.open(url(), '_blank', 'noopener');
    try { if (window.mzEmit) window.mzEmit('bulk:form-open', { via: 'prefilled-link' }); } catch (e) {}
  });
  wrap.appendChild(btn);
})();
